import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Pencil, Trash2, MapPin, Plus, Check, X, Save,
} from 'lucide-react';
import authAxios from '../utils/authAxios';
import CustomerLogoUpload from './CustomerLogoUpload';

// ─── Styled ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  background: var(--white-a55);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid var(--white-a66);
  border-radius: 16px;
  box-shadow: 0 16px 48px var(--shadow-a15);
  padding: 18px 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: var(--black);
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-family: var(--font-heading, sans-serif);
  font-weight: 800;
  font-size: 1.4rem;
  line-height: 1.1;
  margin: 0 0 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  word-break: break-word;
`;

const TitleInput = styled.input`
  font-family: var(--font-heading, sans-serif);
  font-weight: 800;
  font-size: 1.4rem;
  line-height: 1.1;
  width: 100%;
  border: 1px solid var(--shadow-a15);
  border-radius: 8px;
  padding: 6px 10px;
  background: var(--white);
  color: var(--black);
  outline: none;

  &:focus { border-color: var(--green-800); box-shadow: 0 0 0 3px var(--green-a08); }
`;

const Meta = styled.div`
  font-size: 0.82rem;
  color: var(--ink-a60);
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
  margin-left: auto;
`;

const IconBtn = styled.button`
  width: 34px;
  height: 34px;
  border: 1px solid var(--shadow-a08);
  border-radius: 10px;
  background: var(--white-a55);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--black);
  cursor: pointer;
  flex: 0 0 auto;
  font-family: inherit;

  &:hover { background: var(--white); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Divider = styled.div`
  height: 1px;
  background: var(--shadow-a08);
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.10em;
  color: var(--ink-a60);
  font-weight: 700;
`;

const BtnAdd = styled.button`
  height: 30px;
  border: 1px solid var(--green-a30);
  border-radius: 8px;
  background: var(--green-a08);
  color: var(--green-800);
  font-weight: 700;
  font-size: 0.82rem;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: var(--green-a15); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const DestList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: ${({ $surface }) => ($surface === 'modal' ? '320px' : '54vh')};
  overflow-y: auto;
  padding-right: 4px;
`;

const Dest = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: ${({ $editing, $adding }) =>
    $editing || $adding ? 'var(--white)' : 'var(--white-a55)'};
  border: ${({ $editing, $adding }) =>
    $editing
      ? '1px solid var(--green-800)'
      : $adding
      ? '1px dashed var(--green-800)'
      : '1px solid var(--shadow-a05)'};
  box-shadow: ${({ $editing }) => ($editing ? '0 0 0 3px var(--green-a15)' : 'none')};
  border-radius: 10px;
  transition: background 120ms ease;

  &:hover { background: ${({ $editing, $adding }) =>
    $editing || $adding ? 'var(--white)' : 'var(--white-a80)'};
  }
`;

const Pin = styled.div`
  width: 28px;
  height: 28px;
  background: ${({ $accent }) => ($accent ? 'var(--green-a15)' : 'var(--green-a08)')};
  color: var(--green-800);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
`;

const DestName = styled.div`
  font-weight: 600;
  font-size: 0.92rem;
  flex: 1;
  word-break: break-word;
`;

const DestInput = styled.input`
  flex: 1;
  height: 30px;
  border: 1px solid var(--shadow-a15);
  border-radius: 8px;
  padding: 0 10px;
  font: inherit;
  font-weight: 600;
  color: var(--black);
  outline: none;
  background: var(--white);
  text-transform: uppercase;

  &:focus { border-color: var(--green-800); box-shadow: 0 0 0 3px var(--green-a08); }
`;

const RowActions = styled.div`
  display: flex;
  gap: 4px;
  opacity: 0.40;
  transition: opacity 120ms ease;

  ${Dest}:hover &,
  ${Dest}:focus-within & { opacity: 1; }
`;

const RowIconBtn = styled.button`
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  border-radius: 8px;
  color: ${({ $danger }) => ($danger ? 'var(--color-danger)' : 'var(--ink-a60)')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: ${({ $danger }) => ($danger ? 'var(--danger-a10)' : 'var(--shadow-a05)')};
    color: ${({ $danger }) => ($danger ? 'var(--color-danger)' : 'var(--black)')};
  }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SaveBtn = styled.button`
  height: 30px;
  padding: 0 10px;
  border: 0;
  border-radius: 8px;
  background: var(--green-800);
  color: var(--white);
  font-weight: 700;
  font-size: 0.82rem;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: var(--green-900); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CancelIconBtn = styled(RowIconBtn)`
  background: var(--shadow-a05);
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
  padding-top: 12px;
  border-top: 1px solid var(--shadow-a08);
  gap: 8px;
  flex-wrap: wrap;
`;

const FootHint = styled.span`
  font-size: 0.78rem;
  color: var(--ink-a45);
`;

const BtnDangerGhost = styled.button`
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--danger-a30);
  border-radius: 8px;
  background: transparent;
  color: var(--color-danger);
  font-weight: 700;
  font-size: 0.82rem;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: var(--danger-a10); }
  &:focus-visible { outline: 2px solid var(--color-danger); outline-offset: 2px; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PrimaryBtn = styled.button`
  height: 36px;
  padding: 0 16px;
  border: 0;
  border-radius: 10px;
  background: var(--green-800);
  color: var(--white);
  font-weight: 700;
  font-size: 0.88rem;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: inherit;

  &:hover { background: var(--green-900); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ErrorRow = styled.div`
  background: var(--danger-a10);
  color: var(--color-danger);
  border: 1px solid var(--danger-a30);
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 0.85rem;
`;

// ─── Component ───────────────────────────────────────────────────────────────

export default function CustomerDetailPanel({
  mode = 'view',                  // 'view' | 'create'
  group = null,                   // { customer, destinations: Set, ids[], rows[], logo }
  surface = 'panel',              // 'panel' | 'modal'
  onClose,
  refetch,
  onRenamed,                      // async (newName) => void; page updates selectedKey + refetch
  onRequestDeleteDestination,     // (rowId, destinationName) => void
  onRequestDeleteCustomer,        // (rowIds, customerName) => void
}) {
  const isCreate = mode === 'create';

  const [name, setName] = useState(group?.customer || '');
  const [nameEditing, setNameEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [editingDestId, setEditingDestId] = useState(null);
  const [draftValue, setDraftValue] = useState('');
  const [adding, setAdding] = useState(isCreate); // create mode opens with the add row visible
  const [newDest, setNewDest] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [pendingLogo, setPendingLogo] = useState(null);
  const [pendingLogoPreview, setPendingLogoPreview] = useState(null);

  const nameInputRef = useRef(null);
  const newDestInputRef = useRef(null);

  // Focus the name input when opening in create mode
  useEffect(() => {
    if (isCreate) {
      nameInputRef.current?.focus();
    } else {
      setName(group?.customer || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.customer, isCreate]);

  // Reset adding state to true initially in create mode
  useEffect(() => {
    if (isCreate) setAdding(true);
  }, [isCreate]);

  // Build a stable, ordered list of destinations from the group
  const destinations = useMemo(() => {
    if (!group?.rows) return [];
    return [...group.rows]
      .filter((r) => r.UniqueID != null)
      .map((r) => ({ id: r.UniqueID, value: r.Destination || '' }))
      .sort((a, b) => (a.value || '').localeCompare(b.value || ''));
  }, [group?.rows]);

  const destCount = destinations.filter((d) => d.value).length;

  // ── Logo flow ─────────────────────────────────────────────────────────────
  const handleLogoSelected = async (file) => {
    setError(null);
    if (isCreate) {
      // Stash the file; preview locally; upload after the customer row exists
      setPendingLogo(file);
      const reader = new FileReader();
      reader.onload = (e) => setPendingLogoPreview(String(e.target?.result || ''));
      reader.readAsDataURL(file);
      return;
    }
    if (!group?.ids?.length) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      await authAxios.post(`/api/customers/${group.ids[0]}/logo`, fd);
      await refetch();
    } catch (e) {
      console.error('Logo upload failed', e);
      setError('Logo upload failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Destination edit ──────────────────────────────────────────────────────
  const beginEditDest = (id, currentValue) => {
    setEditingDestId(id);
    setDraftValue(currentValue || '');
    setError(null);
  };

  const cancelEditDest = () => {
    setEditingDestId(null);
    setDraftValue('');
  };

  const saveEditDest = async (id) => {
    const next = String(draftValue || '').trim();
    if (!next) { setError('Destination cannot be empty.'); return; }
    // Disallow duplicates within this customer's destinations (excluding the row being edited)
    const conflict = destinations.find(
      (d) => d.id !== id && (d.value || '').trim().toLowerCase() === next.toLowerCase()
    );
    if (conflict) { setError('That destination already exists for this customer.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await authAxios.put(`/api/customers/${id}`, {
        Customer: group.customer,
        Destination: next,
      });
      cancelEditDest();
      await refetch();
    } catch (e) {
      console.error('Destination update failed', e);
      setError('Could not save destination.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Destination add (existing customer) ───────────────────────────────────
  const beginAdd = () => {
    setAdding(true);
    setNewDest('');
    setError(null);
    // focus on next render
    setTimeout(() => newDestInputRef.current?.focus(), 0);
  };

  const cancelAdd = () => {
    if (isCreate) return; // can't cancel adding in create mode
    setAdding(false);
    setNewDest('');
  };

  const saveNewDest = async () => {
    const next = String(newDest || '').trim();
    if (!next) { setError('Destination is required.'); return; }
    const conflict = destinations.find(
      (d) => (d.value || '').trim().toLowerCase() === next.toLowerCase()
    );
    if (conflict) { setError('That destination already exists for this customer.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await authAxios.post('/api/customers', {
        Customer: group.customer,
        Destination: next,
      });
      setAdding(false);
      setNewDest('');
      await refetch();
    } catch (e) {
      console.error('Add destination failed', e);
      setError('Could not add destination.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Customer name rename (view mode only) ─────────────────────────────────
  const beginRename = () => {
    if (isCreate) return;
    setNameEditing(true);
    setNameDraft(group?.customer || '');
    setError(null);
  };

  const cancelRename = () => {
    setNameEditing(false);
    setNameDraft('');
  };

  const saveRename = async () => {
    const next = String(nameDraft || '').trim();
    if (!next) { setError('Customer name is required.'); return; }
    if (next === group.customer) { cancelRename(); return; }
    if (!group?.rows?.length) return;
    setSubmitting(true);
    setError(null);
    try {
      // Apply the new name to every row that currently has this customer name.
      // Sequential to avoid hammering the DB; the cardinality is small.
      for (const row of group.rows) {
        await authAxios.put(`/api/customers/${row.UniqueID}`, {
          Customer: next,
          Destination: row.Destination || '',
        });
      }
      cancelRename();
      // Hand off to the page so it can atomically update selectedKey and
      // refetch — keeps the auto-close logic from firing on the transient
      // window where rows still have the old name but selectedKey is the new.
      if (onRenamed) {
        await onRenamed(next);
      } else {
        await refetch();
      }
    } catch (e) {
      console.error('Rename failed', e);
      setError('Could not rename customer.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Create new customer (full flow) ───────────────────────────────────────
  const saveNewCustomer = async () => {
    const trimmedName = String(name || '').trim();
    const trimmedDest = String(newDest || '').trim();
    if (!trimmedName) { setError('Customer name is required.'); return; }
    if (!trimmedDest) { setError('At least one destination is required.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const res = await authAxios.post('/api/customers', {
        Customer: trimmedName,
        Destination: trimmedDest,
      });
      const newId = res?.data?.id;
      if (pendingLogo && newId) {
        const fd = new FormData();
        fd.append('image', pendingLogo);
        await authAxios.post(`/api/customers/${newId}/logo`, fd);
      }
      await refetch();
      onClose && onClose();
    } catch (e) {
      console.error('Create customer failed', e);
      setError('Could not create customer.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const logoUrl = isCreate ? pendingLogoPreview : group?.logo?.url || null;

  return (
    <Wrap>
      <Top>
        <CustomerLogoUpload
          customerName={isCreate ? name : group?.customer || ''}
          logoUrl={logoUrl}
          onFileSelected={handleLogoSelected}
          size={64}
        />

        <TitleBlock>
          {isCreate ? (
            <TitleInput
              ref={nameInputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Customer name"
              maxLength={120}
              aria-label="Customer name"
            />
          ) : nameEditing ? (
            <Title>
              <TitleInput
                type="text"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveRename();
                  else if (e.key === 'Escape') cancelRename();
                }}
                aria-label="Edit customer name"
                autoFocus
              />
              <SaveBtn type="button" onClick={saveRename} disabled={submitting} aria-label="Save name">
                <Check size={14} aria-hidden="true" /> Save
              </SaveBtn>
              <CancelIconBtn type="button" onClick={cancelRename} aria-label="Cancel rename">
                <X size={14} aria-hidden="true" />
              </CancelIconBtn>
            </Title>
          ) : (
            <Title>
              {group?.customer || ''}
              {!isCreate && (
                <RowIconBtn
                  type="button"
                  onClick={beginRename}
                  aria-label="Rename customer"
                  title="Rename customer"
                >
                  <Pencil size={14} aria-hidden="true" />
                </RowIconBtn>
              )}
            </Title>
          )}
          <Meta>
            {isCreate
              ? 'New customer'
              : `${destCount} destination${destCount === 1 ? '' : 's'}`}
          </Meta>
        </TitleBlock>

        <Actions>
          <IconBtn type="button" onClick={onClose} aria-label="Close detail" title="Close">
            <X size={14} aria-hidden="true" />
          </IconBtn>
        </Actions>
      </Top>

      <Divider />

      <SectionHead>
        <SectionTitle>Destinations</SectionTitle>
        {!isCreate && !adding && (
          <BtnAdd type="button" onClick={beginAdd} disabled={submitting}>
            <Plus size={14} aria-hidden="true" /> Add Destination
          </BtnAdd>
        )}
      </SectionHead>

      {error && <ErrorRow role="alert">{error}</ErrorRow>}

      <DestList $surface={surface}>
        {!isCreate && destinations.map((d) => {
          const isEditing = editingDestId === d.id;
          if (!d.value && !isEditing) {
            // Legacy NULL-destination row: show as placeholder, allow add via the +Add button below.
            return (
              <Dest key={d.id}>
                <Pin><MapPin size={14} aria-hidden="true" /></Pin>
                <DestName style={{ color: 'var(--ink-a45)', fontStyle: 'italic' }}>
                  (No destinations) — use “+ Add Destination” to fill this in
                </DestName>
              </Dest>
            );
          }
          return (
            <Dest key={d.id} $editing={isEditing}>
              <Pin><MapPin size={14} aria-hidden="true" /></Pin>
              {isEditing ? (
                <>
                  <DestInput
                    type="text"
                    value={draftValue}
                    onChange={(e) => setDraftValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEditDest(d.id);
                      else if (e.key === 'Escape') cancelEditDest();
                    }}
                    autoFocus
                    aria-label="Edit destination"
                  />
                  <SaveBtn type="button" onClick={() => saveEditDest(d.id)} disabled={submitting}>
                    <Check size={14} aria-hidden="true" /> Save
                  </SaveBtn>
                  <CancelIconBtn type="button" onClick={cancelEditDest} aria-label="Cancel edit">
                    <X size={14} aria-hidden="true" />
                  </CancelIconBtn>
                </>
              ) : (
                <>
                  <DestName>{d.value}</DestName>
                  <RowActions>
                    <RowIconBtn
                      type="button"
                      onClick={() => beginEditDest(d.id, d.value)}
                      aria-label="Edit destination"
                      title="Edit destination"
                      disabled={submitting}
                    >
                      <Pencil size={14} aria-hidden="true" />
                    </RowIconBtn>
                    <RowIconBtn
                      type="button"
                      $danger
                      onClick={() => onRequestDeleteDestination
                        && onRequestDeleteDestination(d.id, d.value)}
                      aria-label="Delete destination"
                      title="Delete destination"
                      disabled={submitting}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </RowIconBtn>
                  </RowActions>
                </>
              )}
            </Dest>
          );
        })}

        {adding && (
          <Dest $adding>
            <Pin $accent><Plus size={14} aria-hidden="true" /></Pin>
            <DestInput
              ref={newDestInputRef}
              type="text"
              value={newDest}
              onChange={(e) => setNewDest(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (isCreate) saveNewCustomer();
                  else saveNewDest();
                } else if (e.key === 'Escape') {
                  cancelAdd();
                }
              }}
              placeholder="New destination, e.g. SAXONBURG, PA"
              autoFocus={!isCreate}
              aria-label="New destination"
            />
            {!isCreate && (
              <>
                <SaveBtn type="button" onClick={saveNewDest} disabled={submitting}>
                  <Check size={14} aria-hidden="true" /> Add
                </SaveBtn>
                <CancelIconBtn type="button" onClick={cancelAdd} aria-label="Cancel add">
                  <X size={14} aria-hidden="true" />
                </CancelIconBtn>
              </>
            )}
          </Dest>
        )}
      </DestList>

      <Footer>
        {isCreate ? (
          <>
            <FootHint>Logo is optional — click the square to upload</FootHint>
            <div style={{ display: 'flex', gap: 8 }}>
              <BtnDangerGhost type="button" onClick={onClose} disabled={submitting}>
                Cancel
              </BtnDangerGhost>
              <PrimaryBtn type="button" onClick={saveNewCustomer} disabled={submitting}>
                <Save size={14} aria-hidden="true" />
                {submitting ? 'Saving…' : 'Save Customer'}
              </PrimaryBtn>
            </div>
          </>
        ) : (
          <>
            <FootHint>Changes save per row</FootHint>
            <BtnDangerGhost
              type="button"
              onClick={() =>
                onRequestDeleteCustomer
                && group
                && onRequestDeleteCustomer(group.ids, group.customer)
              }
              disabled={submitting}
            >
              <Trash2 size={14} aria-hidden="true" />
              Delete Customer
            </BtnDangerGhost>
          </>
        )}
      </Footer>
    </Wrap>
  );
}
